import type { ProColumns } from "@ant-design/pro-table";
import { ColumnsStateType } from "@ant-design/pro-table/lib/typing";
import { Button, Spin } from "antd";
import AntdTablePro from "components/antdTablePro";
import { debounce } from "lodash";
import AssignWorkerSelect from "pages/orders/AssignWorkerSelect";
import StatusSelect from "pages/orders/StatusSelect";
import ViewComponent from "pages/orders/View";
import WorkerOrderNumber from "pages/orders/WorkerOrderNumber";
import { useInstallationListPaginated } from "hooks/queries/useInstallation";
import { useInstructionSummary } from "hooks/queries/useInstructionSummary";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { SortingOrder } from "types";
import { InstallationSchema, MultiStatus } from "types/installation";
import { getGoogleMapLink } from "utils/global";
import { getIsWithPermission } from "utils/global/installationHelper";
import InvoiceStatusToggle from "./InvoiceStatusToggle";
import MultiOrderPDF from "./MultiOrderPDF";
import { AiOutlineFilePdf } from "react-icons/ai";

export type StatusFilter = Partial<Record<MultiStatus, boolean>>;

type OrderByOptions =
  | "createdAt"
  | "pickupDate"
  | "setupDate"
  | "updatedDateRangeFrom"
  | "zipCode";

type FilterParams = {
  page: number;
  size: number;
  search: string;
  order: SortingOrder;
  orderBy: OrderByOptions;
  includeEmail?: string;
  excludeEmail?: string;
  hasAll?: boolean;
  withPermission?: boolean;
  noSignInstallation?: boolean;
  isImportant?: boolean;
  isAccountingTable?: boolean;
  pickupDateStart?: string;
  pickupDateEnd?: string;
  setupDateStart?: string;
  setupDateEnd?: string;
  combinedSignMode?: boolean;
} & StatusFilter;

const columnsState: ColumnsStateType = {
  persistenceKey: "orderTable",
  persistenceType: "localStorage",
};

export default function OrderTable({
  statusFilter = {},
  rowClassName,
  pageSize = 10,
  order = "ASC",
  orderBy = "updatedDateRangeFrom",
  excludeEmail,
  includeEmail,
  hasAll = false,
  withPermission,
  noSignInstallation,
  isImportant,
  onTotalDataChange,
  isAccountingTable,
  showPdfExport = false,
  pdfTitle = "Orders",
  combinedSignMode,
  defaultSetupDateStart = "",
  defaultSetupDateEnd = "",
  defaultPickupDateStart = "",
  defaultPickupDateEnd = "",
}: {
  pageSize?: number;
  order?: SortingOrder;
  orderBy?: OrderByOptions;
  rowClassName?: string | ((record: InstallationSchema) => string);
  statusFilter?: StatusFilter;
  excludeEmail?: string | undefined;
  includeEmail?: string | undefined;
  hasAll?: boolean;
  withPermission?: boolean;
  noSignInstallation?: boolean;
  isImportant?: boolean;
  onTotalDataChange?: (count: number | undefined) => void;
  isAccountingTable?: boolean;
  showPdfExport?: boolean;
  pdfTitle?: string;
  combinedSignMode?: boolean;
  defaultSetupDateStart?: string;
  defaultSetupDateEnd?: string;
  defaultPickupDateStart?: string;
  defaultPickupDateEnd?: string;
}) {
  const [filterParams, setFilterParams] = useState<FilterParams>({
    page: 1,
    size: pageSize,
    search: "",
    order,
    orderBy,
    includeEmail,
    excludeEmail,
    hasAll,
    withPermission,
    noSignInstallation,
    isImportant,
    isAccountingTable,
    combinedSignMode,
    setupDateStart: defaultSetupDateStart || undefined,
    setupDateEnd: defaultSetupDateEnd || undefined,
    pickupDateStart: defaultPickupDateStart || undefined,
    pickupDateEnd: defaultPickupDateEnd || undefined,
    ...statusFilter,
  });

  const [state, setState] = useState<{
    formModal: boolean;
    defaultValue: InstallationSchema | null;
  }>({
    formModal: false,
    defaultValue: null,
  });

  const [pickupDateStart, setPickupDateStart] = useState(
    defaultPickupDateStart,
  );
  const [pickupDateEnd, setPickupDateEnd] = useState(defaultPickupDateEnd);

  const [setupDateStart, setSetupDateStart] = useState(defaultSetupDateStart);
  const [setupDateEnd, setSetupDateEnd] = useState(defaultSetupDateEnd);

  const printRef = useRef<HTMLDivElement>(null);

  const { isLoading, data } = useInstallationListPaginated(filterParams);
  const instructionSummaryParams = {
    combinedSignMode: combinedSignMode || undefined,
    acceptOrder: filterParams.acceptOrder?.toString(),
    rejectOrder: filterParams.rejectOrder?.toString(),
    failedOrder: filterParams.failedOrder?.toString(),
    requestApproval: filterParams.requestApproval?.toString(),
    permitGranted: filterParams.permitGranted?.toString(),
    approvalSubmittedByCustomer:
      filterParams.approvalSubmittedByCustomer?.toString(),
    installationProtocol: filterParams.installationProtocol?.toString(),
    sendDocumentsToCustomer: filterParams.sendDocumentsToCustomer?.toString(),
    draftCreated: filterParams.draftCreated?.toString(),
    sendInvoice: filterParams.sendInvoice?.toString(),
    invoiceReminded: filterParams.invoiceReminded?.toString(),
    invoicePaid: filterParams.invoicePaid?.toString(),
    closeOrder: filterParams.closeOrder?.toString(),
    erectSigns: filterParams.erectSigns?.toString(),
    collectSigns: filterParams.collectSigns?.toString(),
    excludeEmail: filterParams.excludeEmail || excludeEmail,
    includeEmail: filterParams.includeEmail || includeEmail,
    noSignInstallation:
      filterParams.noSignInstallation?.toString() ??
      noSignInstallation?.toString(),
    withPermission:
      filterParams.withPermission?.toString() ?? withPermission?.toString(),
    search: filterParams.search || undefined,
    setupDateStart: filterParams.setupDateStart || undefined,
    setupDateEnd: filterParams.setupDateEnd || undefined,
    pickupDateStart: filterParams.pickupDateStart || undefined,
    pickupDateEnd: filterParams.pickupDateEnd || undefined,
  };

  const { data: instructionSummary } = useInstructionSummary(
    instructionSummaryParams,
    !!combinedSignMode,
  );

  useEffect(() => {
    // Update defaulValue in model when data is invalidated
    setState((prev) => {
      const selectedInstallation = data?.items.find(
        (installation) => prev.defaultValue?.id === installation.id,
      );

      if (!selectedInstallation) {
        return { ...prev };
      }

      return {
        ...prev,
        defaultValue: selectedInstallation,
      };
    });

    if (onTotalDataChange) {
      onTotalDataChange(data?.count);
    }
  }, [data?.items]);

  const handlePickupDateRangeChange = (start: string, end: string) => {
    setPickupDateStart(start);
    setPickupDateEnd(end);
    setFilterParams({
      ...filterParams,
      page: 1,
      pickupDateStart: start,
      pickupDateEnd: end,
    });
  };

  const handleSetupDateRangeChange = (start: string, end: string) => {
    setSetupDateStart(start);
    setSetupDateEnd(end);
    setFilterParams({
      ...filterParams,
      page: 1,
      setupDateStart: start,
      setupDateEnd: end,
    });
  };

  const { t } = useTranslation();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: pdfTitle,
    pageStyle: `
      @page {
        size: landscape;
      }
    `,
  });

  const displayData = data?.items;

  const columns: ProColumns[] = [
    {
      key: "bookingNumber",
      title: t("orders:bookingNumber"),
      render: (_, row: InstallationSchema) => {
        const isWithPermission = getIsWithPermission(row);
        return (
          <div className="flex justify-between items-center">
            <div style={{ color: row.invoiceReminded ? "red" : "black" }}>
              {row.payment.paymentId}
            </div>
            {isWithPermission && <InvoiceStatusToggle installation={row} />}
          </div>
        );
      },
    },
    {
      key: "firstName",
      title: t("orders:firstName"),
      render: (_, row: InstallationSchema) => {
        return (
          <div>
            {row.additionalInformation?.additionalFirstName ||
              row.customer.firstName}
          </div>
        );
      },
    },
    {
      key: "lastName",
      title: t("orders:lastName"),
      render: (_, row: InstallationSchema) => {
        return (
          <div>
            {row.additionalInformation?.additionalLastName ||
              row.customer.lastName}
          </div>
        );
      },
    },
    {
      key: "companyName",
      title: t("orders:companyName"),
      render: (_, row: InstallationSchema) => {
        return (
          <div>
            {row.additionalInformation?.additionalCompanyName ||
              row.customer.companyName}
          </div>
        );
      },
    },
    {
      key: "blockingAddress",
      title: t("orders:blockingAddress"),
      render: (_, row: InstallationSchema) => {
        const zipCode =
          row.additionalInformation?.additionalZipCode || row.zipCode;
        return (
          <a
            href={getGoogleMapLink(row)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div style={{ width: 150 }}>
              {row.additionalInformation?.additionalStreetAndHouseNumber ||
                row.streetAndHouseNumber}
              , {zipCode ? `${zipCode} ` : ""}
              {row.additionalInformation?.additionalCity || row.city.name}
            </div>
          </a>
        );
      },
    },
    {
      key: "shutOffDate",
      title: t("orders:shutOffDate"),
      valueType: "option",
      render: (_, row: InstallationSchema) => {
        return (
          <div
            className="flex flex-col justify-center"
            style={{ width: 130, minHeight: 80 }}
          >
            {row.updatedDateRangeFrom === row.updatedDateRangeTo ? (
              <div>
                <b>{t("general:on")}: </b>
                {row.updatedDateRangeFrom}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div>
                  <b>{t("general:from")}: </b>
                  {row.updatedDateRangeFrom}
                </div>
                <div>
                  <b>{t("general:to")}: </b>
                  {row.updatedDateRangeTo}
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "setupDate",
      title: t("orders:multiStatus.erectSigns"),
      render: (_, row: InstallationSchema) => {
        return <div style={{ width: 100 }}>{row.setupDate}</div>;
      },
    },
    {
      key: "pickupDate",
      title: t("orders:multiStatus.collectSigns"),
      render: (_, row: InstallationSchema) => {
        return <div style={{ width: 100 }}>{row.pickupDate}</div>;
      },
    },
    {
      key: "email",
      title: t("orders:email"),
      render: (_, row: InstallationSchema) => {
        return (
          <div>
            {row.additionalInformation?.additionalEmail || row.customer.email}
          </div>
        );
      },
    },
    {
      key: "invoiceNumber",
      title: t("invoice:field.invoiceNumber"),
      render: (_, row: InstallationSchema) => {
        return <div>{row.invoice?.invoiceNumber ?? "-"}</div>;
      },
    },
    {
      key: "status",
      title: t("orders:status"),
      render: (_, row: InstallationSchema) => (
        <StatusSelect
          installation={row}
          width={325}
          dropdownClassName="multi-status-dropdown"
        />
      ),
    },
    {
      key: "assignedTo",
      title: t("orders:assignedTo"),
      render: (_, row: InstallationSchema) => {
        return <AssignWorkerSelect row={row} />;
      },
    },
    {
      key: "workerOrderNumber",
      title: t("orders:workerOrderNumber"),
      render: (_, row: InstallationSchema) => {
        return <WorkerOrderNumber row={row} />;
      },
    },
    {
      key: "createdAt",
      title: t("orders:orderDate"),
      dataIndex: "createdAt",
      valueType: "date",
    },
    {
      key: "action",
      title: t("orders:action"),
      valueType: "option",
      render: (_, row: InstallationSchema, index) => [
        <div key={index}>
          <Button
            type="primary"
            onClick={() =>
              setState((prevState) => ({
                ...prevState,
                formModal: true,
                defaultValue: row,
              }))
            }
          >
            {t("main:view")}
          </Button>
        </div>,
      ],
    },
  ];

  const getSearchKeywordsDeb = React.useCallback(
    debounce((value: string) => {
      setFilterParams((prev) => ({
        ...prev,
        page: 1,
        search: value,
      }));
    }, 500),
    [],
  );

  if (isLoading) return <Spin />;

  return (
    <React.Fragment>
      {showPdfExport && (
        <MultiOrderPDF
          ref={printRef}
          data={displayData ?? []}
          title={pdfTitle}
        />
      )}

      <AntdTablePro
        columns={columns}
        rowKey="id"
        dataSource={displayData}
        rowClassName={rowClassName}
        columnsState={columnsState}
        className="order-table"
        onDoubleClick={(record: InstallationSchema) =>
          setState((prevState) => ({
            ...prevState,
            formModal: true,
            defaultValue: record,
          }))
        }
        pagination={
          hasAll
            ? false // No pagination if hasAll is true
            : {
                showSizeChanger: true,
                showQuickJumper: true,
                current: filterParams?.page,
                pageSize: filterParams?.size,
                total: data?.count,
                onChange: (page, pageSize) => {
                  setFilterParams({
                    ...filterParams,
                    page,
                    size: pageSize,
                  });
                },
              }
        }
        inputElement={{
          placeholder: t("main:search"),
          handleChange: (e) => getSearchKeywordsDeb(e.target.value),
          handleSearch: (value) =>
            setFilterParams({ ...filterParams, page: 1, search: value }),
        }}
        dateRangeElement={[
          {
            startDate: setupDateStart,
            endDate: setupDateEnd,
            onRangeChange: handleSetupDateRangeChange,
            placeholder: [
              t("orders:multiStatus.erectSigns") + " " + t("general:from"),
              t("orders:multiStatus.erectSigns") + " " + t("general:to"),
            ],
          },
          {
            startDate: pickupDateStart,
            endDate: pickupDateEnd,
            onRangeChange: handlePickupDateRangeChange,
            placeholder: [
              t("orders:multiStatus.collectSigns") + " " + t("general:from"),
              t("orders:multiStatus.collectSigns") + " " + t("general:to"),
            ],
          },
        ]}
        toolBarRender={[
          ...(showPdfExport
            ? [
                <Button
                  key="pdf-export-btn"
                  type="primary"
                  className="modal-button"
                  onClick={handlePrint}
                >
                  <AiOutlineFilePdf />
                </Button>,
              ]
            : []),
        ]}
      />
      <div style={{ padding: "4px 6px " }}>
        {combinedSignMode &&
          instructionSummary &&
          instructionSummary?.length > 0 && (
            <div style={{ marginTop: 12 }} className="instruction-summary">
              Output:{" "}
              {instructionSummary?.map((s, idx) => {
                const net = s.net;
                const text =
                  net === 0
                    ? `${s.code}: 0`
                    : `${s.code}: ${net > 0 ? "+" : "-"}${Math.abs(net)}`;
                return (
                  <span key={`${s.code}${s.net}`}>
                    {text}
                    {idx < instructionSummary.length - 1 ? ", " : ""}
                  </span>
                );
              })}
            </div>
          )}
      </div>

      {state.formModal && (
        <ViewComponent
          visible={state.formModal}
          state={state}
          defaultValue={state.defaultValue!}
          onHide={() =>
            setState((prevState) => ({ ...prevState, formModal: false }))
          }
        />
      )}
    </React.Fragment>
  );
}
